import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import css from "./CustomSelect.module.css";
import { BsFillCaretDownFill } from "react-icons/bs";
import clsx from "clsx";

const CustomSelect = ({
  field,
  options = [],
  setFieldValue,
  placeholderName,
  isOpen,
  toggleDropdown,
  isDisabled,
  carDataByPlate,
  recordId,
  isAddCarModal,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const dropDownRef = useRef(null);
  const initialMissingValueRef = useRef(null);

  useEffect(() => {
    if (!field.value) return;

    const exists = options.some(
      opt =>
        String(opt.value).toLowerCase() === String(field.value).toLowerCase()
    );

    if (!exists && !initialMissingValueRef.current) {
      initialMissingValueRef.current = field.value;
    }
  }, [field.value, options]); // 👈 тільки при першому рендері

  const normalizedOptions = useMemo(() => {
    let extended = [...options];

    const missing = initialMissingValueRef.current;

    if (missing) {
      const exists = extended.some(
        opt => String(opt.value).toLowerCase() === String(missing).toLowerCase()
      );

      if (!exists) {
        extended.push({
          value: missing,
          label: missing,
        });
      }
    }

    return extended.sort((a, b) =>
      String(a.label).localeCompare(String(b.label), "uk", {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [options, field.value]);

  const selectedOption = useMemo(() => {
    return (
      normalizedOptions.find(
        option =>
          String(option.value).toLowerCase() ===
          String(field.value).toLowerCase()
      ) || null
    );
  }, [normalizedOptions, field.value]);

  const wasManuallyChanged = useRef(false);

  useEffect(() => {
    if (recordId) return;
    if (!options.length) return;
    if (wasManuallyChanged.current) return;

    const autoFillMap = {
      make: carDataByPlate?.make,
      model: carDataByPlate?.model,
      year: carDataByPlate?.year,
    };

    const autoFillValue = autoFillMap[field.name];
    if (!autoFillValue) return;

    const match = options.find(
      option =>
        String(option.value).toLowerCase() ===
        String(autoFillValue).toLowerCase()
    );

    if (match && field.value !== match.value) {
      setFieldValue(field.name, match.value);
    }
  }, [
    field.name,
    options,
    carDataByPlate,
    recordId,
    setFieldValue,
    field.value,
  ]);

  const handleSelect = useCallback(
    option => {
      wasManuallyChanged.current = true;
      setFieldValue(field.name, option?.value || "");
      setSearchQuery("");
      toggleDropdown();
    },
    [field.name, setFieldValue, toggleDropdown]
  );

  const handleDropdownClick = e => {
    e.stopPropagation();
    if (!isDisabled) {
      toggleDropdown(!isOpen);
    }
  };

  const filteredOptions = normalizedOptions.filter(option =>
    option.label
      ? option.label
          .toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      : false
  );

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropDownRef.current && !dropDownRef.current.contains(e.target)) {
        toggleDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleDropdown]);

  return (
    <div className={css.customSelect} ref={dropDownRef}>
      <div
        className={clsx(css.customSelectControl, {
          [css.customSelectControlOpen]: isOpen,
          [css.customSelectControlInAddCarModal]: isAddCarModal,
        })}
        onClick={handleDropdownClick}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <input
            type="text"
            value={searchQuery}
            onChange={e => {
              const val = e.target.value;
              setSearchQuery(val);

              if (field.name === "year") {
                const numeric = val.replace(/\D/g, "");
                setSearchQuery(numeric);
                setFieldValue(field.name, numeric);
              }
            }}
            className={css.customSelectInput}
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span
            className={clsx({
              [css.customSelectPlaceholder]: !field.value,
              [css.customSelectInputSelected]: field.value,
            })}
          >
            {selectedOption
              ? selectedOption.label
              : field.value || placeholderName}
          </span>
        )}

        <span
          className={clsx(css.customSelectArrow, {
            [css.customSelectArrowOpen]: isOpen,
          })}
          onClick={handleDropdownClick}
        >
          <BsFillCaretDownFill />
        </span>
      </div>

      {isOpen && (
        <div className={css.customSelectMenu}>
          <ul>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <li
                  key={option.value}
                  className={clsx(css.customSelectOption, {
                    [css.customSelectOptionSelected]:
                      selectedOption?.value === option.value,
                  })}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li className={css.customSelectOption}>Нічого не знайдено</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
